"use client";

/**
 * The one card on Home that is selling something rather than listing it.
 *
 * Script review is the only thing in the portal a creator does not already
 * know they can do. Everything else on this screen answers a question they
 * arrived with — what am I owed, what needs a new take, where is the brief —
 * and a row in a list is the right shape for an answer. This is the opposite:
 * it has to make the case before anyone goes looking, which is why it is wide,
 * why it carries a picture, and why it says what it does in a sentence instead
 * of a four-word label.
 *
 * IT WAS A ROW. "Check your script" sat in Before you post between two
 * destinations of the same weight, which is exactly how a feature nobody has
 * heard of stays unheard of.
 *
 * THE WHOLE CARD IS THE BUTTON and the pill is drawn, not nested. A button
 * inside a button is invalid and the browser's repair of it is not something
 * to design around; the pill is a `span` that looks like the affordance, and
 * the press belongs to the surface under it — the same arrangement as the
 * destination cards.
 *
 * THE ART IS A FILM, five seconds of a script arriving at a screen and being
 * read, and it is the whole card rather than a panel on one side of it. As a
 * 42% column it was a picture pasted next to a card; behind everything it is
 * the card, which is what the reference does and what makes this read as a
 * poster instead of a list item with an illustration.
 *
 * SO THIS CARD DOES NOT FOLLOW THE THEME. Its ground is a night sky in both
 * light and dark, so its text is light in both — `text-nf-ds-white` and a
 * scrim, not `--nf-ds-fg`. That is the same reasoning `Chip` already records
 * for a label on a video frame: a role token describes a surface the system
 * controls, and this surface is a photograph. Everything structural still
 * comes from tokens; only the foreground is pinned.
 */

import { ArrowRight } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { BADGE, TONE } from "@/components/ui/design-system/badge-styles";
import { Body, CardTitle } from "@/components/ui/design-system/text";
import { usePrefersReducedMotion } from "@/components/ui/design-system/use-reduced-motion";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { PROSE, ROW_PRESS } from "./chrome";

/**
 * The loop, which is decoration and behaves like it.
 *
 * NO `autoplay` ATTRIBUTE. Playback starts in an effect, so a person who has
 * asked for less movement never gets a frame of it — with the attribute the
 * browser starts before any of our code runs and the best we could do is stop
 * it afterwards, which is the thing the preference exists to prevent. They
 * get the poster, which is the film's first frame, so the card is composed
 * either way rather than empty.
 *
 * Muted and `playsInline` are what make it play at all: every browser blocks
 * autoplay with sound, and iOS takes an unmuted video fullscreen. It is also
 * `aria-hidden` and out of the tab order — it is a picture, and the card
 * around it is the control.
 *
 * The left edge is masked rather than butted against the copy. Without it the
 * film is a rectangle pasted into the corner of a card; faded, the two halves
 * read as one surface.
 */
function ScriptFilm() {
  const reduced = usePrefersReducedMotion();
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    if (reduced) {
      el.pause();
      return;
    }
    // Rejects when the browser declines — a data-saver mode, a background
    // tab. The poster is already the fallback, so there is nothing to do.
    void el.play().catch(() => {});
  }, [reduced]);

  return (
    <video
      ref={video}
      src="/one/script-review.mp4"
      poster="/one/script-review.jpg"
      loop
      muted
      playsInline
      preload="metadata"
      aria-hidden="true"
      tabIndex={-1}
      className="absolute inset-0 size-full object-cover"
    />
  );
}

export function ScriptReviewCard({
  onOpen,
  badge,
  art,
  className,
}: {
  onOpen: () => void;
  /** A word beside the title — "Beta", "New". Off unless someone means it. */
  badge?: string;
  /** Real artwork, when there is some. Replaces the drawn script. */
  art?: ReactNode;
  /** Placement only. */
  className?: string;
}) {
  const t = useCreatorTranslations("home");
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "group/promo relative w-full overflow-hidden rounded-nf-ds-card text-left outline-hidden",
        // Behind the film, so the card has a surface for the moment before
        // the poster paints and if the file never arrives.
        "bg-nf-ds-tone-brand-bg ring-1 ring-nf-ds-surface-card-ring ring-inset",
        "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
        ROW_PRESS,
        className,
      )}
    >
      {art ?? <ScriptFilm />}

      {/* The scrim, which is the only reason the copy is readable over a
          picture nobody chose for legibility. It runs left to right rather
          than covering the frame: the words are on the left, the television
          is on the right, and a flat wash over the whole thing would dim the
          subject to protect text that is not on top of it. */}
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-nf-ds-ink/92 via-nf-ds-ink/60 to-transparent"
      />

      {/* Two thirds, not a little over half. The copy column was 58% of a
          540-point card, which is 313 points — the sentence broke across two
          lines and the card grew a row to hold them, so a card meant to read
          as a wide poster came out tall and crowded. The film is 2.4:1 now
          rather than 1.74, cropped nearer the shape the card actually is, so
          there is room to give and the television still clears the words. */}
      {/* Top and bottom, not centred. The grid gives this card the height of
          the squares beside it, and a block floating in the middle of that
          leaves equal dead space above and below — the card reads as too tall
          for what is in it. Pinned, the height becomes the composition: the
          name at the top where a poster's name goes, the action on the floor
          where the eye finishes. */}
      {/* `h-full` is what makes `justify-between` mean anything. A button
          centres its content box when the box is shorter than the button,
          and the grid gives this one the height of the squares beside it —
          so without a height the block just sat in the middle and the two
          ends had nothing to travel to. */}
      <span className="relative flex h-full min-h-[176px] max-w-[78%] flex-col items-start justify-between gap-nf-ds-4 p-nf-ds-6 sm:max-w-[66%]">
        <span className="flex flex-col items-start gap-nf-ds-2">
          <span className="flex items-center gap-nf-ds-2">
            {/* The card-title role, the same one every card on this screen
                now uses. It was the section role at 20/600, on the argument
                that this block has no heading above it — which made it the
                odd one out in a row of cards, and 600 is lighter than the
                700 the guidelines put on a card title, so it managed to be
                bigger and softer than its neighbours at once. */}
            <CardTitle as="span" className="text-nf-ds-white">
              {t("scriptReview.title")}
            </CardTitle>
            {/* `idle`, not a brand fill: `badge-styles` has no brand tone on
              purpose — a badge filled with brand is a button, whatever its
              height. */}
            {badge ? (
              <span className={cn(BADGE, TONE.idle)}>{badge}</span>
            ) : null}
          </span>

          {/*
          WHAT YOU GET, not how to use it.

          It read "Paste a script before you film. Framework flags anything
          that would get it rejected." Three things were wrong with that.
          It opened on the mechanic, which the button under it already
          states. It named the system, which the creator has no reason to
          care about. And it sold only the half that says no — the review
          also returns a suggested hook and a list of things that would help
          the video land, which is the half a creator paid on performance
          actually wants.

          One sentence, both outputs, and the timing that makes it useful.
        */}
          <Body
            as="span"
            // The `body` role, not `PROSE`. `PROSE` is 16 on a phone and 14
            // past rail width — a deliberate bump for reading prose on a small
            // screen, and wrong here: it made this card's one sentence larger
            // on mobile than the same sentence on the tiles beside it, which
            // never had the bump. One size at every width.
            className="text-pretty text-nf-ds-white/75"
          >
            {t("scriptReview.body")}
          </Body>
        </span>

        {/* Drawn, not nested — see the note above. Glass rather than a ring
            on the card's fill, because there is no fill here to ring: it sits
            on a photograph and has to separate from whatever is behind it. */}
        <span
          className={cn(
            "inline-flex h-nf-ds-control items-center gap-nf-ds-tight rounded-nf-ds-button px-nf-ds-4",
            "font-nf-ds-sans text-nf-ds-md font-[500] leading-none text-nf-ds-white",
            "bg-nf-ds-white/10 ring-1 ring-nf-ds-white/30 ring-inset backdrop-blur-sm",
          )}
        >
          {t("scriptReview.action")}
          <ArrowRight
            className="size-[16px] transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover/promo:translate-x-[2px] motion-reduce:transition-none motion-reduce:group-hover/promo:translate-x-0"
            strokeWidth={2}
          />
        </span>
      </span>
    </button>
  );
}
